import { NBBDeliveryMode } from "@features/newBelgium/deliveryModes";
import { Transformer } from "@services/api";

import { Address } from "./Address";
import { AsyncResource } from "./AsyncResource";
import { OrderVariant } from "./OrderVariant";

type DateString = string;

export type OrderType = "inventory" | "pre-order" | "on-demand";
export type OrderStatus =
  | "draft"
  | "submitted"
  | "approval-review"
  | "approved"
  | "canceled"
  | "error";

export type CheckoutType = null | "stripe" | "budget" | "default";

/**
 * Known custom fields. Plugins augment this interface for fields they own.
 */
// eslint-disable-next-line @typescript-eslint/no-empty-object-type
export interface OrderCustomFields {}

export type Order = {
  address: Address;
  addressExternalId?: string;
  approvedAt: DateString;
  approvedByUserName: string | null;
  attn?: string;
  budgetName: string | null;
  cancelationNote: string | null;
  cancelationType: "order" | "denial" | null;
  canceledByUserName?: string;
  canceledAt: DateString;
  channelName?: string | null;
  checkoutType: CheckoutType;
  expeditedDate: DateString | null;
  id: string;
  isExpedited: boolean;
  canDeliverEarly: boolean;
  notes?: string;
  programName?: string;
  referenceNumber: string | null;
  reviewedAt: DateString;
  reviewedByUserName: string | null;
  status: OrderStatus;
  submittedAt: DateString;
  submittedBy: null | string;
  territoryId: number;
  territoryName: string;
  totalActualShippingCost: number;
  totalEstimatedShippingCost: number;
  totalActualTax: number | null;
  totalEstimatedTax: number;
  totalPrice: number;
  totalPromotionDiscount: number;
  totalQuantity: number;
  type: OrderType;
  userId: string;
  userName: string;
  userEmail: string;
  userExternalId: string | null;
  isForEvent: boolean;
  orderWindowName: string | null;
  orderSetId: number;
  budget: AsyncResource<"budget"> | null;
  deliveryMode: NBBDeliveryMode | null;
  userCanApprove: boolean;
  customFields: OrderCustomFields;
};

export type OrderWithOrderVariants = Order & {
  orderVariants: OrderVariant[];
};

const orderTransformer: Transformer = {
  type: ["order", "orders"],
  deserialize: (attr) => ({
    ...attr,
    customFields: attr.customFields ?? {},
    totalActualShippingCost: Number(attr.totalActualShippingCost),
    totalEstimatedShippingCost: Number(attr.totalEstimatedShippingCost),
    totalEstimatedTax: Number(attr.totalEstimatedTax),
    totalActualTax: attr.totalActualTax && Number(attr.totalActualTax),
    totalPrice: Number(attr.totalPrice),
    totalPromotionDiscount: Number(attr.totalPromotionDiscount),
  }),
};

export default orderTransformer;
